import { useEffect, useId, useState, type ReactNode } from "react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { studentIdsForAccount, type AuthRole } from "../domain/auth";
import { sessionIdentity, type SessionIdentity } from "../domain/sessionStatus";
import { useAuth } from "../state/AuthProvider";
import { LanguageToggle, useLocale } from "../state/i18n";
import { useLayout } from "../state/LayoutProvider";
import { useStudents } from "../state/students";
import liftLogo from "../assets/lift-logo.png";

export function BrandMark({
  to = "/",
  size = "nav",
}: {
  to?: string;
  size?: "nav" | "hero";
}) {
  return (
    <Link className={`brand brand--${size}`} to={to} aria-label="Lift home">
      <img
        className="brand-logo"
        src={liftLogo}
        alt="Lift"
        width={size === "hero" ? 160 : 44}
        height={size === "hero" ? 159 : 44}
      />
    </Link>
  );
}

export function PrototypeBanner() {
  const { t } = useLocale();
  const { sim } = useLayout();
  return (
    <div className="prototype-banner">
      <span>{sim ? t("banner.phoneSim") : t("banner.title")}</span>
    </div>
  );
}

function AppHeader({
  children,
  signInTo = "/signin",
}: {
  children: ReactNode;
  signInTo?: string;
}) {
  const { t } = useLocale();
  const { phone } = useLayout();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const navId = useId();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <header className="topnav">
      <BrandMark />
      <div className="topnav-tools">
        <LanguageToggle />
        <SessionControl signInTo={signInTo} when="signedOut" />
        <button
          type="button"
          className="nav-menu-btn"
          aria-expanded={open}
          aria-controls={navId}
          onClick={() => setOpen((value) => !value)}
        >
          {open ? t("nav.closeMenu") : t("nav.menu")}
        </button>
      </div>
      <nav
        id={navId}
        className={`nav-links${phone && open ? " nav-links--open" : ""}`}
      >
        {children}
      </nav>
      <SessionControl signInTo={signInTo} when="signedIn" />
    </header>
  );
}

export function SignOutButton({ className = "session-action" }: { className?: string }) {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { t } = useLocale();
  return (
    <button
      type="button"
      className={className}
      onClick={() => {
        signOut();
        navigate("/");
      }}
    >
      {t("nav.signOut")}
    </button>
  );
}

const ROLE_LABEL: Record<AuthRole, "signin.tagParent" | "signin.tagStudent" | "signin.tagStaff"> = {
  parent: "signin.tagParent",
  student: "signin.tagStudent",
  staff: "signin.tagStaff",
};

export function SessionStatus({
  identity,
  size = "nav",
  signInTo = "/signin",
  statusLabel,
  signInLabel,
  signOutLabel,
  roleLabel = "",
  onSignOut,
}: {
  identity: SessionIdentity;
  size?: "nav" | "hero";
  signInTo?: string;
  statusLabel: string;
  signInLabel: string;
  signOutLabel: string;
  roleLabel?: string;
  onSignOut?: () => void;
}) {
  if (!identity.signedIn) {
    return (
      <NavLink className={`session-action session-action--${size}`} to={signInTo}>
        {signInLabel}
      </NavLink>
    );
  }
  const meta = [roleLabel, identity.name === identity.email ? "" : identity.email]
    .filter(Boolean)
    .join(" · ");
  return (
    <div
      className={`signed-in signed-in--${size}`}
      role="status"
      data-signed-in="true"
      aria-label={`${statusLabel}: ${identity.name}, ${identity.email}`}
    >
      <span className="signed-in__status">{statusLabel}</span>
      <span className="signed-in__who">
        <strong>{identity.name}</strong>
        {meta ? <span className="signed-in__meta">{meta}</span> : null}
      </span>
      <button type="button" className="signed-in__out" onClick={onSignOut}>
        {signOutLabel}
      </button>
    </div>
  );
}

export function SessionControl({
  signInTo = "/signin",
  when = "always",
  size = "nav",
}: {
  signInTo?: string;
  when?: "always" | "signedIn" | "signedOut";
  size?: "nav" | "hero";
}) {
  const navigate = useNavigate();
  const { account, session, signOut } = useAuth();
  const { t } = useLocale();
  if (!session) {
    if (when === "signedIn") return null;
    return (
      <SessionStatus
        identity={{ signedIn: false }}
        size={size}
        signInTo={signInTo}
        statusLabel={t("session.signedIn")}
        signInLabel={t("nav.signIn")}
        signOutLabel={t("nav.signOut")}
      />
    );
  }
  if (when === "signedOut") return null;
  const identity = sessionIdentity(
    account ?? {
      given: "",
      family: "",
      email: session.email,
      role: session.role,
    },
  );
  return (
    <SessionStatus
      identity={identity}
      size={size}
      signInTo={signInTo}
      statusLabel={t("session.signedIn")}
      signInLabel={t("nav.signIn")}
      signOutLabel={t("nav.signOut")}
      roleLabel={identity.signedIn ? t(ROLE_LABEL[identity.role]) : ""}
      onSignOut={() => {
        signOut();
        navigate("/");
      }}
    />
  );
}

export function ChildSwitcher({ studentId }: { studentId: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { account } = useAuth();
  const { studentsByIds } = useStudents();
  const { t } = useLocale();
  if (!account || account.role !== "parent") return null;
  const siblings = studentsByIds(studentIdsForAccount(account));
  if (siblings.length < 2) return null;
  return (
    <label className="child-switcher">
      <span className="meta">{t("student.switch")}</span>
      <select
        value={studentId}
        onChange={(event) => {
          const next = event.target.value;
          navigate(location.pathname.replace(`/student/${studentId}`, `/student/${next}`));
        }}
      >
        {siblings.map((student) => (
          <option key={student.id} value={student.id}>
            {student.given} {student.family}
          </option>
        ))}
      </select>
    </label>
  );
}

export function StudentNav({ studentId }: { studentId: string }) {
  const { account } = useAuth();
  const { t } = useLocale();
  return (
    <AppHeader>
      <NavLink to="/" end>
        {t("nav.home")}
      </NavLink>
      {account?.role === "parent" ? (
        <NavLink to="/family">{t("nav.family")}</NavLink>
      ) : null}
      <NavLink to={`/student/${studentId}`}>{t("student.landing")}</NavLink>
      <NavLink to={`/student/${studentId}/profile`}>{t("nav.profile")}</NavLink>
      <NavLink to={`/student/${studentId}/events`}>{t("nav.events")}</NavLink>
    </AppHeader>
  );
}

export function PublicNav() {
  const { account, pathForAccount, session } = useAuth();
  const { t } = useLocale();
  return (
    <AppHeader>
      <NavLink to="/" end>
        {t("nav.home")}
      </NavLink>
      {!account || account.role === "parent" ? (
        <NavLink to="/register" end>
          {t("nav.register")}
        </NavLink>
      ) : null}
      <NavLink to="/events">{t("nav.events")}</NavLink>
      {session && account ? (
        <NavLink to={pathForAccount(account)}>{t("nav.account")}</NavLink>
      ) : null}
    </AppHeader>
  );
}

export function StaffNav() {
  return (
    <AppHeader signInTo="/signin?role=staff">
      <NavLink to="/" end>
        Home
      </NavLink>
      <NavLink to="/staff" end>
        Schools
      </NavLink>
      <NavLink to="/staff/forms">Forms</NavLink>
      <NavLink to="/staff/events">Events</NavLink>
      <NavLink to="/staff/inbox">Inbox</NavLink>
      <NavLink to="/staff/settings">Settings</NavLink>
    </AppHeader>
  );
}

export function EventSeatSummary({
  registered,
  capacity,
  remaining,
  waitlisted,
  waitlistEnabled,
  audience,
}: {
  registered: number;
  capacity: number;
  remaining: number;
  waitlisted: number;
  waitlistEnabled: boolean;
  audience: string;
}) {
  const { t } = useLocale();
  const full = remaining <= 0;
  const detail = !full
    ? t("events.spots", { count: remaining })
    : waitlistEnabled && waitlisted > 0
      ? t("events.waitlistLine", { count: waitlisted })
      : waitlistEnabled
        ? t("events.waitlistOpen")
        : t("events.full");

  return (
    <div className="wire">
      <div className="row">
        <div>
          <div className="meta">{t("events.capacity")}</div>
          <strong>
            {t("events.registeredCount", { registered, capacity })}
          </strong>
          <div className="anno">{detail}</div>
          {full && waitlistEnabled ? (
            <div className="anno">{t("events.waitlistHow")}</div>
          ) : null}
        </div>
        <div className="tag">{audience}</div>
      </div>
    </div>
  );
}

export function EventDescriptionBlock({
  text,
  label,
}: {
  text: string;
  label: string;
}) {
  if (!text) return null;
  return (
    <div className="wire" style={{ marginTop: "1rem" }}>
      <div className="meta">{label}</div>
      <p className="event-description">{text}</p>
    </div>
  );
}

